import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit in-ground swimming pool on a 3x2 tile patch: a low white coping
// rim ring around a water slab, with a small chrome ladder on one long side.
// Long axis runs along X.
const COPING = "#f2f0ea";
const WATER = "#6fa8c0";
const STEEL = "#aab2b8";

const W = 2.8; // along X
const D = 1.8; // along Z
const RIM_W = 0.25;
const RIM_H = 0.12;
const INNER_W = W - 2 * RIM_W;
const INNER_D = D - 2 * RIM_W;

export const swimmingPool = defineModel({
  id: "swimming-pool",
  batchId: "modern",
  name: "Swimming pool",
  tags: ["modern", "outdoor", "pool", "garden", "water", "swimming"],
  summary: "An in-ground pool with white coping and a chrome ladder.",
  description:
    "An in-ground swimming pool of the Modern Living kit on a 3x2 tile " +
    "patch: a 2.8 x 1.8 m low-profile pool with a white coping rim at 0.12 " +
    "m, a water surface at 0.1 m and a small chrome ladder on one long side.",
  tiles: { w: 3, h: 2 },
  footprint: 1.9,
  parts(): Part[] {
    const ladderRail = (x: number): Part => ({
      geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.55, 6),
      color: STEEL,
      position: [x, 0.275, INNER_D / 2 - 0.1],
    });
    const ladderRung = (y: number): Part => ({
      geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.32, 6),
      color: STEEL,
      position: [0.9, y, INNER_D / 2 - 0.1],
      rotation: [0, 0, Math.PI / 2],
    });
    return [
      // Coping rim ring: two long boards along X, two short along Z.
      {
        geometry: new THREE.BoxGeometry(W, RIM_H, RIM_W),
        color: COPING,
        position: [0, RIM_H / 2, -(D - RIM_W) / 2],
      },
      {
        geometry: new THREE.BoxGeometry(W, RIM_H, RIM_W),
        color: COPING,
        position: [0, RIM_H / 2, (D - RIM_W) / 2],
      },
      {
        geometry: new THREE.BoxGeometry(RIM_W, RIM_H, INNER_D),
        color: COPING,
        position: [-(W - RIM_W) / 2, RIM_H / 2, 0],
      },
      {
        geometry: new THREE.BoxGeometry(RIM_W, RIM_H, INNER_D),
        color: COPING,
        position: [(W - RIM_W) / 2, RIM_H / 2, 0],
      },
      // Water surface at 0.1.
      {
        geometry: new THREE.BoxGeometry(INNER_W, 0.08, INNER_D),
        color: WATER,
        position: [0, 0.06, 0],
      },
      // Chrome ladder on the +Z long side.
      ladderRail(0.75),
      ladderRail(1.05),
      ladderRung(0.18),
      ladderRung(0.38),
    ];
  },
  animations: [],
});
